/**
 * MapLibre Layer Style Definitions for NoteSymbolLayer
 *
 * Two stacked symbol layers — solid teardrop pin in the per-note color +
 * IconNote glyph in a fixed contrast color (no border / halo).
 *
 * Reused on two sources: the unified `map-cluster-source` (main map, where
 * the `featureKind === 'note'` filter scopes the layers to notes) and the
 * non-clustered `note-markers-source` (NoteMiniMap; `point_count` is never
 * present and `featureKind` is set by `useNoteGeoJson`, so the same filter
 * passes trivially).
 *
 * @module components/map/NoteSymbolLayer/styles/noteSymbolLayerStyles
 */

import type {
  SymbolLayerSpecification,
  ExpressionSpecification,
} from '@maplibre/maplibre-gl-style-spec';

import {
  ZOOM_SCALE_MIN,
  ZOOM_SCALE_MAX,
  SCALE_MIN as SCALE_FACTOR_MIN,
  SCALE_MAX as SCALE_FACTOR_MAX,
} from '@/utils/markerScaling';
import {
  SHAPE_SPRITE_SIZE,
  NOTE_PIN_SPRITE_KEY,
  NOTE_ICON_SPRITE_KEY,
} from '@/components/map/symbolSprites';
import { SYMBOL_LAYER_COLORS } from '@/constants/colorPresets';
import {
  MAP_CLUSTER_FEATURE_KIND_NOTE,
  buildUnclusteredFeatureFilter,
} from '@/components/map/MapClusterLayer/featureKind';

// ===== LAYER & SOURCE IDENTIFIERS =====

export const NOTE_LAYER_IDS = {
  MARKER_PIN: 'note-marker-pin',
  MARKER_ICON: 'note-marker-icon',
} as const;

/** Note icon glyph color — white on the saturated pin reads in both themes. */
const NOTE_ICON_COLOR = SYMBOL_LAYER_COLORS.border.light;

export const NOTE_SOURCE_ID = 'note-markers-source';

const unclusteredNoteFilter = buildUnclusteredFeatureFilter(
  MAP_CLUSTER_FEATURE_KIND_NOTE
);

// ===== SIZE CONSTANTS =====

/**
 * Base pin size in pixels — matches the previous DOM marker (32 px outer + 4 px
 * shadow halo). Sprite canvas is 64 px so the multiplier is base/canvas.
 */
const NOTE_BASE_SIZE_PX = 36;

const NOTE_SIZE_RATIO = NOTE_BASE_SIZE_PX / SHAPE_SPRITE_SIZE;

/** Icon size at min/max zoom (matches Task layer's interpolation curve). */
const NOTE_ICON_SIZE_MIN = SCALE_FACTOR_MIN * NOTE_SIZE_RATIO;
const NOTE_ICON_SIZE_MAX = SCALE_FACTOR_MAX * NOTE_SIZE_RATIO;

/** Selected note pin is scaled up so the active marker stays visible amid others. */
const SELECTED_SCALE_FACTOR = 1.2;

// ===== ICON-SIZE EXPRESSION =====

/**
 * Composite icon size — interpolated by zoom; selected pins use a 1.2× value
 * baked into each stop output.
 *
 * MapLibre constraint: `["zoom"]` may only appear as the direct input to a
 * top-level `step` / `interpolate` expression. Wrapping `interpolate` in
 * `["*", ..., ["case", ...]]` is rejected at addLayer-time. Pushing the
 * `case` (no `zoom` inside, only `["get", "isSelected"]`) into each stop's
 * output value satisfies the rule.
 */
const noteIconSizeExpression: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  ZOOM_SCALE_MIN,
  [
    'case',
    ['get', 'isSelected'],
    NOTE_ICON_SIZE_MIN * SELECTED_SCALE_FACTOR,
    NOTE_ICON_SIZE_MIN,
  ],
  ZOOM_SCALE_MAX,
  [
    'case',
    ['get', 'isSelected'],
    NOTE_ICON_SIZE_MAX * SELECTED_SCALE_FACTOR,
    NOTE_ICON_SIZE_MAX,
  ],
];

// ===== LAYER SPECS =====

/** Shared layout for both note layers — same anchor + size keeps the glyph
 *  aligned over the pin head across zoom + selection states. */
const sharedNoteLayout = {
  'icon-size': noteIconSizeExpression,
  'icon-allow-overlap': true,
  'icon-anchor': 'bottom',
  'icon-padding': 0,
} as const;

/** Pin layer — solid teardrop in the per-feature color. Anchored at bottom so
 *  the pin tail sits on the geo coordinate. */
export const noteMarkerPinLayer: Omit<SymbolLayerSpecification, 'source'> = {
  id: NOTE_LAYER_IDS.MARKER_PIN,
  type: 'symbol',
  filter: unclusteredNoteFilter,
  layout: { ...sharedNoteLayout, 'icon-image': NOTE_PIN_SPRITE_KEY },
  paint: {
    'icon-color': ['get', 'color'] as ExpressionSpecification,
  },
};

/** Icon overlay — IconNote glyph drawn on top of the pin in a contrast color. */
export const noteMarkerIconLayer: Omit<SymbolLayerSpecification, 'source'> = {
  id: NOTE_LAYER_IDS.MARKER_ICON,
  type: 'symbol',
  filter: unclusteredNoteFilter,
  layout: { ...sharedNoteLayout, 'icon-image': NOTE_ICON_SPRITE_KEY },
  paint: {
    'icon-color': NOTE_ICON_COLOR,
  },
};
